import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Check, CheckCheck, Trash2, X, Filter, ListTodo, MessageSquare, FileText, Rocket } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import api from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [typeCounts, setTypeCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isOpen, setIsOpen] = useState(false);
  const prevUnreadCount = useRef(0);
  const navigate = useNavigate();

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const params = {};
      if (activeFilter !== 'all') {
        params.notification_type = activeFilter;
      }
      const response = await api.get('/notifications', { params });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
      setTypeCounts(response.data.type_counts || {});
      
      // Show toast for new notifications (when count increases)
      const newUnreadCount = response.data.unread_count || 0;
      if (newUnreadCount > prevUnreadCount.current) {
        const newCount = newUnreadCount - prevUnreadCount.current;
        // Get the latest notification for the toast message
        const latestNotif = response.data.notifications?.[0];
        if (latestNotif && prevUnreadCount.current >= 0) {
          toast.info(
            <div className="flex flex-col">
              <span className="font-medium">{latestNotif.title}</span>
              <span className="text-sm text-muted-foreground">{latestNotif.message?.substring(0, 50)}...</span>
            </div>,
            {
              duration: 5000,
              action: {
                label: 'View',
                onClick: () => {
                  if (latestNotif.link) {
                    window.location.href = latestNotif.link;
                  }
                }
              }
            }
          );
        }
      }
      prevUnreadCount.current = newUnreadCount;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [activeFilter]);

  // Poll for notifications every 10 seconds for more real-time feel
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Refetch when filter changes
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [activeFilter, isOpen, fetchNotifications]);

  // Mark single notification as read
  const markAsRead = async (notificationId, e) => {
    if (e) e.stopPropagation();
    try {
      await api.post('/notifications/mark-read', { notification_ids: [notificationId] });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const params = activeFilter !== 'all' ? { notification_type: activeFilter } : {};
      await api.post('/notifications/mark-all-read', null, { params });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      fetchNotifications(); // Refresh counts
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId, e) => {
    if (e) e.stopPropagation();
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      fetchNotifications(); // Refresh counts
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  // Delete all notifications
  const deleteAllNotifications = async () => {
    try {
      const params = activeFilter !== 'all' ? { notification_type: activeFilter } : {};
      await api.delete('/notifications', { params });
      setNotifications([]);
      fetchNotifications(); // Refresh counts
      toast.success('All notifications deleted');
    } catch (error) {
      toast.error('Failed to delete notifications');
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.notification_id);
    }
    if (notification.link) {
      setIsOpen(false);
      navigate(notification.link);
    }
  };

  // Get icon for notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'task': return <ListTodo className="w-4 h-4 text-blue-500" />;
      case 'chat': return <MessageSquare className="w-4 h-4 text-green-500" />;
      case 'form': return <FileText className="w-4 h-4 text-purple-500" />;
      case 'launchpad': return <Rocket className="w-4 h-4 text-orange-500" />;
      default: return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  // Format relative time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const filterTabs = [
    { value: 'all', label: 'All', icon: Bell },
    { value: 'task', label: 'Tasks', icon: ListTodo, count: typeCounts.task },
    { value: 'chat', label: 'Chat', icon: MessageSquare, count: typeCounts.chat },
    { value: 'form', label: 'Forms', icon: FileText, count: typeCounts.form },
    { value: 'launchpad', label: 'Links', icon: Rocket, count: typeCounts.launchpad },
  ];

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell-btn"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500 hover:bg-red-500"
              data-testid="notification-badge"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 sm:w-96 p-0"
        data-testid="notification-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={markAllAsRead}
                className="text-xs h-7 px-2"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3 h-3 mr-1" />
                Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={deleteAllNotifications}
                className="text-xs h-7 px-2 text-muted-foreground hover:text-destructive"
                data-testid="delete-all-notifications-btn"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Delete all
              </Button>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <Tabs value={activeFilter} onValueChange={setActiveFilter} className="w-full">
          <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0 overflow-x-auto">
            {filterTabs.map(tab => (
              <TabsTrigger 
                key={tab.value}
                value={tab.value}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#4A9CC7] data-[state=active]:bg-transparent px-3 py-2 text-xs"
              >
                <tab.icon className="w-3 h-3 mr-1" />
                {tab.label}
                {tab.count > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                    {tab.count}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <ScrollArea className="h-[300px]">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Bell className="w-8 h-8 mb-2 opacity-50" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map(notification => (
                  <div
                    key={notification.notification_id}
                    className={`p-3 hover:bg-muted cursor-pointer transition-colors ${
                      !notification.is_read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-${notification.notification_id}`}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.notification_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${!notification.is_read ? 'font-medium' : ''}`}>
                            {notification.title}
                          </p>
                          {!notification.is_read && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                          {notification.message}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                      <div className="flex-shrink-0 flex flex-col gap-1">
                        {!notification.is_read && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={(e) => markAsRead(notification.notification_id, e)}
                            title="Mark as read"
                          >
                            <Check className="w-3 h-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={(e) => deleteNotification(notification.notification_id, e)}
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </Tabs>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
